import type { TQuestionAnswer } from '$/types/components/questions';
import type { IInputQuestion } from './_question';
import type { IAutocompleteProps } from '../Autocomplete/autocomplete';

import React from 'react';
import { Controller } from 'react-hook-form';

import Autocomplete from '../Autocomplete';
import SelectAnswer from '../SelectAnswer';

import Subtitle from './elements/Subtitle';
import Title from './elements/Title';

import getControlError from '$/hooks/getControlError';
import useElb from '$/hooks/useElb';
import { TRACKING_ENTITY } from '$/utils/tracking/consts';

interface IAutocompleteInputQuestion
  extends IInputQuestion,
    Omit<IAutocompleteProps, 'value' | 'onChange'> {
  onValueSelected?: () => void;
  subTitle?: string;
  list?: TQuestionAnswer[];
  selectFieldElbEntity?: string;
  selectFieldElbAction?: string;
  searchedFieldElbEntity?: string;
  searchedFieldElbAction?: string;
}

function transformToAutocompleteValue<T extends string | number>(
  value: TQuestionAnswer<T> | TQuestionAnswer<T>[] | undefined | null
): TQuestionAnswer<T>[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
}

const AutocompleteInputQuestion = ({
  title,
  subTitle,
  control,
  name,
  list,
  onValueSelected,
  buttonProps,
  selectFieldElbEntity = TRACKING_ENTITY.STUDY_FINANCING,
  selectFieldElbAction,
  searchedFieldElbEntity = TRACKING_ENTITY.STUDY_FINANCING,
  searchedFieldElbAction,
  ...props
}: IAutocompleteInputQuestion) => (
  <Controller
    control={control}
    name={name}
    defaultValue={[]}
    render={({ field, fieldState }) => {
      const { sendTrackingEvent } = useElb();
      const error = getControlError(control, name, props.errorMessage);

      const onChange = (data: TQuestionAnswer[]) => {
        field.onChange(data);
        if (data?.length > 0 && onValueSelected) {
          onValueSelected();
        }
      };

      const onSelectAnswerChange = (data: TQuestionAnswer[]) => {
        if (selectFieldElbEntity && selectFieldElbAction) {
          sendTrackingEvent(selectFieldElbEntity, selectFieldElbAction);
        }
        onChange(data);
      };

      const onSearchedAnswerChange = (data: TQuestionAnswer[]) => {
        if (searchedFieldElbEntity && searchedFieldElbAction) {
          sendTrackingEvent(searchedFieldElbEntity, searchedFieldElbAction);
        }
        onChange(data);
      };
      return (
        <div id={field.name}>
          {title && <Title>{title}</Title>}
          {list && (
            <SelectAnswer
              fieldProps={{
                name: field.name,
                value: field.value,
                onChange: onSelectAnswerChange,
              }}
              list={list}
              maxElements={1}
              buttonProps={{
                variant: 'answer',
              }}
            />
          )}
          {subTitle && <Subtitle>{subTitle}</Subtitle>}
          <Autocomplete
            {...field}
            {...fieldState}
            value={field.value.filter(
              (data: TQuestionAnswer) => !list?.find((d) => d.id === data.id)
            )}
            onValueSelect={onSearchedAnswerChange}
            replaceSearch
            maxElements={1}
            {...props}
            buttonProps={{
              as: 'button',
              variant: 'autoCompleteAnswer',
            }}
            errorMessage={error.message}
          />
        </div>
      );
    }}
  />
);

export default AutocompleteInputQuestion;
